import { isPlatform } from '@ionic/react';
import { Buffer } from 'buffer';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    empresaAlterar,
    empresaConfiguracoesCarregar,
    onboardingAtualizarEtapa,
    requestService,
} from '../../../../../Utils/Services';

// ============================================================================
// useImpressaoStore
// ----------------------------------------------------------------------------
// Camada central de persistência da tela de Configurações de Impressão.
//
// Princípios:
// 1. Hydrate idempotente por `loja` (não sobrescreve edições em curso).
// 2. Campos de DISPOSITIVO -> localStorage síncrono (imediato), com namespace
//    `impressao:{loja}:X` E espelho na chave legada global (para preservar
//    compatibilidade com `EscPosGenerator.ts` e outras telas que leem global).
// 3. Campos de EMPRESA -> autosave debounced + fila serializada (1 PUT por vez,
//    debounces se sobrepõem coalescem em uma única requisição).
// 4. `impressorasAdicionais` (lista compartilhada cross-platform) -> re-fetch
//    + re-merge antes de cada PUT, para reduzir risco de sobrescrever
//    conexões de outras plataformas/dispositivos concorrentes.
// 5. Toggles mantêm exclusividade entre Local/Rede/BLE/SPP no mesmo dispositivo.
// 6. Desabilitar um toggle NÃO apaga IDs/IPs (preserva config para reativação).
// 7. Onboarding `impressao=concluido` é marcado uma única vez por sessão,
//    no primeiro autosave bem-sucedido.
// ============================================================================

export type ConexaoImpressora = {
    tipo: 'local' | 'rede' | 'ble' | 'spp';
    localName?: string;
    redeIp?: string;
    redePorta?: string;
    bluetoothDeviceId?: string;
    bluetoothSPPAddress?: string;
};

export type ImpressoraAdicional = {
    nome: string;
    conexoes: ConexaoImpressora[];
};

// Chaves que ficam por DISPOSITIVO (localStorage)
const DEVICE_BOOL_KEYS = [
    'impressoraLocalHabilitar',
    'impressoraRedeHabilitar',
    'impressoraBluetoothHabilitar',
    'impressoraBluetoothSPPHabilitar',
] as const;

const DEVICE_STR_KEYS = [
    'impressoraLocalName',
    'impressoraBluetoothDeviceId',
    'impressoraBluetoothName',
    'impressoraBluetoothSPPAddress',
    'impressoraBluetoothSPPName',
] as const;

function nsKey(loja: string, key: string) {
    return `impressao:${loja}:${key}`;
}

function readDeviceStr(loja: string, key: string): string {
    // Preferência: namespaced. Fallback: chave global legada.
    // const ns = window.localStorage.getItem(nsKey(loja, key));
    // if (ns !== null) return ns;
    return window.localStorage.getItem(key) || '';
}

function readDeviceBool(loja: string, key: string): boolean {
    // const ns = window.localStorage.getItem(nsKey(loja, key));
    // if (ns !== null) return ns === 'true';
    return window.localStorage.getItem(key) === 'true';
}

function writeDeviceStr(loja: string, key: string, value: string) {
    // Dupla escrita: namespaced + global (compatibilidade).
    // window.localStorage.setItem(nsKey(loja, key), value);
    window.localStorage.setItem(key, value);
}

function writeDeviceBool(loja: string, key: string, value: boolean) {
    const v = value ? 'true' : 'false';
    // window.localStorage.setItem(nsKey(loja, key), v);
    window.localStorage.setItem(key, v);
}

// Converte estrutura flat antiga em aninhada (mantém compatibilidade com banco).
function converterFlatParaAninhada(impressorasFlat: any[]): ImpressoraAdicional[] {
    if (impressorasFlat.length > 0 && impressorasFlat[0].conexoes) {
        return impressorasFlat as ImpressoraAdicional[];
    }
    const grupos: Record<string, ImpressoraAdicional> = {};
    impressorasFlat.forEach((imp: any, index: number) => {
        const nome = imp.nome || `Impressora ${index + 2}`;
        if (!grupos[nome]) grupos[nome] = { nome, conexoes: [] };

        let tipoDetectado: ConexaoImpressora['tipo'] | null = imp.tipo || null;
        if (!tipoDetectado) {
            if (imp.localName) tipoDetectado = 'local';
            else if (imp.redeIp && imp.redePorta) tipoDetectado = 'rede';
            else if (imp.bluetoothDeviceId) tipoDetectado = 'ble';
            else if (imp.bluetoothSPPAddress) tipoDetectado = 'spp';
        }
        if (tipoDetectado) {
            grupos[nome].conexoes.push({
                tipo: tipoDetectado,
                localName: tipoDetectado === 'local' ? imp.localName || '' : '',
                redeIp: tipoDetectado === 'rede' ? imp.redeIp || '' : '',
                redePorta: tipoDetectado === 'rede' ? imp.redePorta || '' : '',
                bluetoothDeviceId: tipoDetectado === 'ble' ? imp.bluetoothDeviceId || '' : '',
                bluetoothSPPAddress: tipoDetectado === 'spp' ? imp.bluetoothSPPAddress || '' : '',
            });
        }
    });
    return Object.values(grupos);
}

// Tipos compatíveis com a plataforma atual (igual lógica original).
function tiposPlataformaAtual(): Array<ConexaoImpressora['tipo']> {
    const isMobile = !isPlatform('mobileweb') && isPlatform('mobile');
    const isAndroid = isPlatform('android');
    if (isMobile) return isAndroid ? ['rede', 'ble', 'spp'] : ['rede', 'ble'];
    return ['local'];
}

// Filtra impressorasAdicionais para a plataforma atual (igual lógica original).
function filtrarParaPlataforma(impressoras: ImpressoraAdicional[]): ImpressoraAdicional[] {
    const tipos = tiposPlataformaAtual();
    return impressoras
        .map((imp) => ({
            ...imp,
            conexoes: imp.conexoes.filter((c) => tipos.includes(c.tipo)),
        }))
        .filter((imp) => imp.conexoes.length > 0);
}

// Merge cross-platform: preserva conexões de outras plataformas vindas do banco.
function mergeCrossPlatform(
    impressorasDoBanco: any[],
    impressorasLocais: ImpressoraAdicional[],
): ImpressoraAdicional[] {
    const tipos = tiposPlataformaAtual();
    const aninhadasDoBanco = converterFlatParaAninhada(impressorasDoBanco || []);
    const merged: ImpressoraAdicional[] = [];

    // 1) Mantém conexões do banco que NÃO são da plataforma atual.
    aninhadasDoBanco.forEach((imp) => {
        const conexoesOutras = imp.conexoes.filter((c) => !tipos.includes(c.tipo));
        if (conexoesOutras.length > 0) {
            merged.push({ nome: imp.nome, conexoes: conexoesOutras });
        }
    });

    // 2) Adiciona/funde conexões locais (da plataforma atual).
    impressorasLocais.forEach((local) => {
        const existente = merged.find((m) => m.nome === local.nome);
        if (existente) {
            existente.conexoes.push(...local.conexoes);
        } else {
            merged.push({ nome: local.nome, conexoes: [...local.conexoes] });
        }
    });
    return merged;
}

// Snapshot do estado de empresa para comparação/serialização.
type EmpresaState = {
    impressoraLargura: string;
    tamanhoFonte: string;
    impressaoAutomatica: boolean;
    impressaoAutomaticaIfood: boolean;
    impressaoDuasVias: boolean;
    imprimirMesaParcial: boolean;
    multiImpressao: number;
    impressoraRedeIp: string;
    impressoraRedePorta: string;
    impressorasAdicionais: ImpressoraAdicional[];
};

type HookProps = {
    loja: string;
    history: any;
    onboardingData: any;
    setOnboardingData: (data: any) => void;
    onError?: (mensagem: string) => void;
};

export function useImpressaoStore({
    loja,
    history,
    onboardingData,
    setOnboardingData,
    onError,
}: HookProps) {
    // ---------- Estado: campos compartilhados (empresa) ----------
    const [impressoraLargura, setImpressoraLarguraState] = useState('');
    const [tamanhoFonte, setTamanhoFonteState] = useState<string>('normal');
    const [impressaoAutomatica, setImpressaoAutomaticaState] = useState(false);
    const [impressaoAutomaticaIfood, setImpressaoAutomaticaIfoodState] = useState(false);
    const [impressaoDuasVias, setImpressaoDuasViasState] = useState(false);
    const [imprimirMesaParcial, setImprimirMesaParcialState] = useState(false);
    const [multiImpressao, setMultiImpressaoState] = useState<number>(1);
    const [impressoraRedeIp, setImpressoraRedeIpState] = useState('');
    const [impressoraRedePorta, setImpressoraRedePortaState] = useState('');
    const [impressorasAdicionais, setImpressorasAdicionaisState] = useState<ImpressoraAdicional[]>([]);

    // ---------- Estado: campos por dispositivo (localStorage) ----------
    const [impressoraLocalHabilitar, setImpressoraLocalHabilitarState] = useState(false);
    const [impressoraLocalName, setImpressoraLocalNameState] = useState('');
    const [impressoraRedeHabilitar, setImpressoraRedeHabilitarState] = useState(false);
    const [impressoraBluetoothHabilitar, setImpressoraBluetoothHabilitarState] = useState(false);
    const [impressoraBluetoothDeviceId, setImpressoraBluetoothDeviceIdState] = useState('');
    const [impressoraBluetoothName, setImpressoraBluetoothNameState] = useState('');
    const [impressoraBluetoothSPPHabilitar, setImpressoraBluetoothSPPHabilitarState] = useState(false);
    const [impressoraBluetoothSPPAddress, setImpressoraBluetoothSPPAddressState] = useState('');
    const [impressoraBluetoothSPPName, setImpressoraBluetoothSPPNameState] = useState('');

    const [hydrated, setHydrated] = useState(false);
    const [loading, setLoading] = useState(true);

    // ---------- Refs de controle ----------
    const hydratedLojaRef = useRef<string | null>(null);
    const hydratedRef = useRef(false);
    const empresaStateRef = useRef<EmpresaState | null>(null);
    const debounceTimerRef = useRef<any>(null);
    const inFlightRef = useRef<boolean>(false);
    const pendingSaveRef = useRef<boolean>(false);
    const onboardingMarkedRef = useRef<boolean>(false);
    // Cache do `impressorasAdicionais` original do banco (carregado no hydrate,
    // atualizado após cada PUT bem-sucedido). Evita um GET por save apenas para
    // preservar conexões cross-platform.
    const impressorasBancoRef = useRef<any[]>([]);

    const retryCountRef = useRef(0);
    const MAX_RETRIES = 3;

    // Mantém ref atual do estado empresa para uso assíncrono dentro do flush.
    useEffect(() => {
        empresaStateRef.current = {
            impressoraLargura,
            tamanhoFonte,
            impressaoAutomatica,
            impressaoAutomaticaIfood,
            impressaoDuasVias,
            imprimirMesaParcial,
            multiImpressao,
            impressoraRedeIp,
            impressoraRedePorta,
            impressorasAdicionais,
        };
    }, [
        impressoraLargura,
        tamanhoFonte,
        impressaoAutomatica,
        impressaoAutomaticaIfood,
        impressaoDuasVias,
        imprimirMesaParcial,
        multiImpressao,
        impressoraRedeIp,
        impressoraRedePorta,
        impressorasAdicionais,
    ]);

    // ============================== HYDRATE ===================================
    const hydrate = useCallback(() => {
        if (!loja) return;
        if (hydratedLojaRef.current === loja) {
            // Já hidratado para esta loja: não sobrescreve edições.
            return;
        }
        hydratedLojaRef.current = loja;
        setLoading(true);

        const url = empresaConfiguracoesCarregar.url
            .replace('{id}', loja.replaceAll(/#/g, '%23'))
            .concat('?filtro=', Buffer.from(loja).toString('base64'));

        requestService(
            url,
            {
                method: empresaConfiguracoesCarregar.method,
                headers: [['token', window.localStorage.getItem('token')]],
                credentials: 'include',
            },
            history,
            (response: any) => {
                const item = response?.Item || {};

                // ---------- Empresa (API) ----------
                setImpressoraLarguraState(item.impressoraLargura || '');
                setTamanhoFonteState(item.tamanhoFonte || 'normal');
                setImpressaoAutomaticaState(!!item.impressaoAutomatica);
                setImpressaoAutomaticaIfoodState(!!item.impressaoAutomaticaIfood);
                setImpressaoDuasViasState(!!item.impressaoDuasVias);
                setImprimirMesaParcialState(!!item.imprimirMesaParcial);
                setMultiImpressaoState(item.multiImpressao || 1);
                setImpressoraRedeIpState(item.impressoraRedeIp || '');
                setImpressoraRedePortaState(
                    item.impressoraRedePorta ? String(item.impressoraRedePorta) : '',
                );

                const impressorasOriginais = item.impressorasAdicionais || [];
                impressorasBancoRef.current = impressorasOriginais;
                const aninhadas = converterFlatParaAninhada(impressorasOriginais);
                setImpressorasAdicionaisState(filtrarParaPlataforma(aninhadas));

                // ---------- Dispositivo (localStorage com fallback legado) ----------
                const localHab = readDeviceBool(loja, 'impressoraLocalHabilitar');
                const localName = readDeviceStr(loja, 'impressoraLocalName');
                const redeHab = readDeviceBool(loja, 'impressoraRedeHabilitar');
                const bleHab = readDeviceBool(loja, 'impressoraBluetoothHabilitar');
                const bleId = readDeviceStr(loja, 'impressoraBluetoothDeviceId');
                const bleName = readDeviceStr(loja, 'impressoraBluetoothName');
                const sppHab = readDeviceBool(loja, 'impressoraBluetoothSPPHabilitar');
                const sppAddr = readDeviceStr(loja, 'impressoraBluetoothSPPAddress');
                const sppName = readDeviceStr(loja, 'impressoraBluetoothSPPName');

                setImpressoraLocalHabilitarState(localHab);
                setImpressoraLocalNameState(localName);
                setImpressoraRedeHabilitarState(redeHab);
                setImpressoraBluetoothHabilitarState(bleHab);
                setImpressoraBluetoothDeviceIdState(bleId);
                setImpressoraBluetoothNameState(bleName);
                setImpressoraBluetoothSPPHabilitarState(sppHab);
                setImpressoraBluetoothSPPAddressState(sppAddr);
                setImpressoraBluetoothSPPNameState(sppName);

                // Garante que as chaves globais reflitam a loja atual
                // (corrige caso onde dispositivo trocou de empresa).
                writeDeviceBool(loja, 'impressoraLocalHabilitar', localHab);
                writeDeviceStr(loja, 'impressoraLocalName', localName);
                writeDeviceBool(loja, 'impressoraRedeHabilitar', redeHab);
                writeDeviceBool(loja, 'impressoraBluetoothHabilitar', bleHab);
                writeDeviceStr(loja, 'impressoraBluetoothDeviceId', bleId);
                writeDeviceStr(loja, 'impressoraBluetoothName', bleName);
                writeDeviceBool(loja, 'impressoraBluetoothSPPHabilitar', sppHab);
                writeDeviceStr(loja, 'impressoraBluetoothSPPAddress', sppAddr);
                writeDeviceStr(loja, 'impressoraBluetoothSPPName', sppName);

                hydratedRef.current = true;
                setHydrated(true);
                setLoading(false);
            },
            (error: any) => {
                console.log('[useImpressaoStore] hydrate erro', error);
                // Reseta guard para permitir retry em próxima entrada.
                hydratedLojaRef.current = null;
                setLoading(false);
                history?.replace?.('/login/');
            },
        );
    }, [loja, history]);

    // ============================== AUTOSAVE EMPRESA ===========================
    const performSave = useCallback(async () => {
        if (!hydratedRef.current) return;
        if (!loja) return;
        if (inFlightRef.current) {
            // Já há um PUT em voo; marca pendente para nova rodada.
            pendingSaveRef.current = true;
            return;
        }
        const snapshot = empresaStateRef.current;
        if (!snapshot) return;

        inFlightRef.current = true;

        // Usa cache em memória do `impressorasAdicionais` do banco (alimentado no
        // hydrate e atualizado após cada PUT bem-sucedido). Não faz GET por save.
        const impressorasMescladas = mergeCrossPlatform(
            impressorasBancoRef.current || [],
            snapshot.impressorasAdicionais,
        );

        const objectRequest = {
            impressoraLargura: snapshot.impressoraLargura,
            impressoraRedeIp: snapshot.impressoraRedeIp,
            impressoraRedePorta: snapshot.impressoraRedePorta
                ? parseInt(snapshot.impressoraRedePorta, 10)
                : 0,
            impressaoAutomaticaIfood: snapshot.impressaoAutomaticaIfood,
            impressaoAutomatica: snapshot.impressaoAutomatica,
            impressaoDuasVias: snapshot.impressaoDuasVias,
            multiImpressao: snapshot.multiImpressao,
            impressorasAdicionais: impressorasMescladas,
            imprimirMesaParcial: snapshot.imprimirMesaParcial,
            tamanhoFonte: snapshot.tamanhoFonte,
        };

        const url = empresaAlterar.url
            .replace('{id}', loja.replaceAll('#', '%23'))
            .concat('?tipo=impressao');

        requestService(
            url,
            {
                method: empresaAlterar.method,
                body: JSON.stringify(objectRequest),
                headers: [['token', window.localStorage.getItem('token')]],
                credentials: 'include',
            },
            history,
            (response: any) => {
                inFlightRef.current = false;
                retryCountRef.current = 0;

                if (response) {
                    // Atualiza cache do banco com o que acabamos de persistir,
                    // mantendo o merge consistente nos próximos saves.
                    impressorasBancoRef.current = impressorasMescladas;

                    // Marca etapa de onboarding uma vez por sessão (sem disparar a cada save).
                    if (
                        !onboardingMarkedRef.current &&
                        onboardingData &&
                        !onboardingData.onboarding_concluido
                    ) {
                        onboardingMarkedRef.current = true;
                        const urlOnb = onboardingAtualizarEtapa.url.replace(
                            '{empresa_id}',
                            onboardingData.empresa_id.replaceAll('#', '%23'),
                        );
                        requestService(
                            urlOnb,
                            {
                                method: onboardingAtualizarEtapa.method,
                                body: JSON.stringify({ etapa_id: 'impressao', status: 'concluido' }),
                                headers: [['token', window.localStorage.getItem('token')]],
                                credentials: 'include',
                            },
                            history,
                            (r: any) => {
                                if (r) setOnboardingData(r);
                            },
                            () => {
                                // Permite nova tentativa numa próxima sessão se falhar.
                                onboardingMarkedRef.current = false;
                            },
                        );
                    }
                } else {
                    onError?.('Ocorreu um erro ao salvar as configurações.');
                }

                // Se houve mudanças durante o PUT, dispara novo save.
                if (pendingSaveRef.current) {
                    pendingSaveRef.current = false;
                    void performSave();
                }
            },
            (error: any) => {
                console.log('[useImpressaoStore] save erro', error);
                inFlightRef.current = false;

                if (retryCountRef.current < MAX_RETRIES) {
                    retryCountRef.current += 1;
                    onError?.(`Falha ao salvar. Tentativa ${retryCountRef.current} de ${MAX_RETRIES}...`);
                    setTimeout(() => {
                        void performSave();
                    }, 3000);
                } else {
                    retryCountRef.current = 0; // reseta para próxima interação do usuário
                    onError?.('Não foi possível salvar. Verifique sua conexão e tente novamente.');
                }
            },
        );
    }, [loja, history, onboardingData, setOnboardingData, onError]);

    // Agenda autosave com debounce configurável.
    const scheduleSave = useCallback(
        (delayMs: number) => {
            if (!hydratedRef.current) return;
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = setTimeout(() => {
                debounceTimerRef.current = null;
                void performSave();
            }, delayMs);
        },
        [performSave],
    );

    // Flush imediato (ao sair da tela, por exemplo).
    const flushNow = useCallback(() => {
        if (!hydratedRef.current) return;
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
            void performSave();
        } else if (pendingSaveRef.current) {
            pendingSaveRef.current = false;
            void performSave();
        }
    }, [performSave]);

    // Ao desmontar, garante flush (caso haja debounce pendente).
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
                if (hydratedRef.current) void performSave();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ============================== SETTERS EMPRESA ============================
    // Toggles/selects: debounce 400ms. Inputs de texto: debounce 1000ms.
    const TOGGLE_DEBOUNCE = 400;
    const INPUT_DEBOUNCE = 1000;

    const setImpressoraLargura = (v: string) => {
        setImpressoraLarguraState(v);
        scheduleSave(TOGGLE_DEBOUNCE);
    };
    const setTamanhoFonte = (v: string) => {
        setTamanhoFonteState(v);
        scheduleSave(TOGGLE_DEBOUNCE);
    };
    const setImpressaoAutomatica = (v: boolean) => {
        setImpressaoAutomaticaState(v);
        scheduleSave(TOGGLE_DEBOUNCE);
    };
    const setImpressaoAutomaticaIfood = (v: boolean) => {
        setImpressaoAutomaticaIfoodState(v);
        scheduleSave(TOGGLE_DEBOUNCE);
    };
    const setImpressaoDuasVias = (v: boolean) => {
        setImpressaoDuasViasState(v);
        scheduleSave(TOGGLE_DEBOUNCE);
    };
    const setImprimirMesaParcial = (v: boolean) => {
        setImprimirMesaParcialState(v);
        scheduleSave(TOGGLE_DEBOUNCE);
    };
    const setImpressoraRedeIp = (v: string) => {
        setImpressoraRedeIpState(v);
        scheduleSave(INPUT_DEBOUNCE);
    };
    const setImpressoraRedePorta = (v: string) => {
        setImpressoraRedePortaState(v);
        scheduleSave(INPUT_DEBOUNCE);
    };
    const setImpressorasAdicionais = (lista: ImpressoraAdicional[]) => {
        setImpressorasAdicionaisState(lista);
        // Recalcula multiImpressao consistentemente.
        const totalConexoes = lista.reduce((acc, imp) => acc + imp.conexoes.length, 0);
        setMultiImpressaoState(totalConexoes > 0 ? totalConexoes + 1 : 1);
        scheduleSave(TOGGLE_DEBOUNCE);
    };

    // ============================== SETTERS DISPOSITIVO ========================
    // Persistem em localStorage IMEDIATAMENTE (síncrono).
    // Exclusividade entre os 4 toggles é aplicada aqui.

    const desligarOutrosToggles = (manter: 'local' | 'rede' | 'ble' | 'spp') => {
        if (manter !== 'local') {
            setImpressoraLocalHabilitarState(false);
            writeDeviceBool(loja, 'impressoraLocalHabilitar', false);
        }
        if (manter !== 'rede') {
            setImpressoraRedeHabilitarState(false);
            writeDeviceBool(loja, 'impressoraRedeHabilitar', false);
        }
        if (manter !== 'ble') {
            setImpressoraBluetoothHabilitarState(false);
            writeDeviceBool(loja, 'impressoraBluetoothHabilitar', false);
        }
        if (manter !== 'spp') {
            setImpressoraBluetoothSPPHabilitarState(false);
            writeDeviceBool(loja, 'impressoraBluetoothSPPHabilitar', false);
        }
    };

    const setImpressoraLocalHabilitar = (v: boolean) => {
        setImpressoraLocalHabilitarState(v);
        writeDeviceBool(loja, 'impressoraLocalHabilitar', v);
        if (v) desligarOutrosToggles('local');
        // NÃO limpa impressoraLocalName ao desligar (preserva config).
    };
    const setImpressoraLocalName = (v: string) => {
        setImpressoraLocalNameState(v);
        writeDeviceStr(loja, 'impressoraLocalName', v);
    };

    const setImpressoraRedeHabilitar = (v: boolean) => {
        setImpressoraRedeHabilitarState(v);
        writeDeviceBool(loja, 'impressoraRedeHabilitar', v);
        if (v) desligarOutrosToggles('rede');
    };

    const setImpressoraBluetoothHabilitar = (v: boolean) => {
        setImpressoraBluetoothHabilitarState(v);
        writeDeviceBool(loja, 'impressoraBluetoothHabilitar', v);
        if (v) desligarOutrosToggles('ble');
        // NÃO limpa deviceId/name ao desligar (preserva config).
    };
    const setImpressoraBluetoothDeviceId = (v: string) => {
        setImpressoraBluetoothDeviceIdState(v);
        writeDeviceStr(loja, 'impressoraBluetoothDeviceId', v);
    };
    const setImpressoraBluetoothName = (v: string) => {
        setImpressoraBluetoothNameState(v);
        writeDeviceStr(loja, 'impressoraBluetoothName', v);
    };

    const setImpressoraBluetoothSPPHabilitar = (v: boolean) => {
        setImpressoraBluetoothSPPHabilitarState(v);
        writeDeviceBool(loja, 'impressoraBluetoothSPPHabilitar', v);
        if (v) desligarOutrosToggles('spp');
        // NÃO limpa address/name ao desligar (preserva config).
    };
    const setImpressoraBluetoothSPPAddress = (v: string) => {
        setImpressoraBluetoothSPPAddressState(v);
        writeDeviceStr(loja, 'impressoraBluetoothSPPAddress', v);
    };
    const setImpressoraBluetoothSPPName = (v: string) => {
        setImpressoraBluetoothSPPNameState(v);
        writeDeviceStr(loja, 'impressoraBluetoothSPPName', v);
    };

    // Adicionar função no hook
    const resetHydration = useCallback(() => {
        hydratedLojaRef.current = null;
        if (!inFlightRef.current) { hydratedRef.current = false; }
        setHydrated(false);
        setLoading(true); // ← garantia de spinner na re-entrada
    }, []);

    return {
        // Estado
        impressoraLargura,
        tamanhoFonte,
        impressaoAutomatica,
        impressaoAutomaticaIfood,
        impressaoDuasVias,
        imprimirMesaParcial,
        multiImpressao,
        impressoraRedeIp,
        impressoraRedePorta,
        impressorasAdicionais,
        impressoraLocalHabilitar,
        impressoraLocalName,
        impressoraRedeHabilitar,
        impressoraBluetoothHabilitar,
        impressoraBluetoothDeviceId,
        impressoraBluetoothName,
        impressoraBluetoothSPPHabilitar,
        impressoraBluetoothSPPAddress,
        impressoraBluetoothSPPName,
        hydrated,
        loading,

        // Setters empresa (autosave debounced)
        setImpressoraLargura,
        setTamanhoFonte,
        setImpressaoAutomatica,
        setImpressaoAutomaticaIfood,
        setImpressaoDuasVias,
        setImprimirMesaParcial,
        setImpressoraRedeIp,
        setImpressoraRedePorta,
        setImpressorasAdicionais,

        // Setters dispositivo (localStorage síncrono)
        setImpressoraLocalHabilitar,
        setImpressoraLocalName,
        setImpressoraRedeHabilitar,
        setImpressoraBluetoothHabilitar,
        setImpressoraBluetoothDeviceId,
        setImpressoraBluetoothName,
        setImpressoraBluetoothSPPHabilitar,
        setImpressoraBluetoothSPPAddress,
        setImpressoraBluetoothSPPName,

        setLoading,
        // Controles
        hydrate,
        flushNow,

        resetHydration,
    };
}
